import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { lazyInject } from '@app/shared/decorators';
import { E2E_ATTRIBUTES } from '@app/shared/types/enums/e2e-attributes.enum';
import { getUpdatedFilterList } from '@app/shared/utils/filter-utils';
import {
  chain,
  clone,
  forEach,
  isArray,
  isEmpty,
  isEqual,
  isNil,
  reduce,
} from 'lodash';
import { BaseComponent } from '../base-component/base.component';
import { FILTER_PANEL_TYPES } from './filter-panel-types.enum';
import { FILTER_PANEL_UPDATE_TYPES } from './filter-panel-update-types.enum';
import {
  FilterPanelUpdates,
  IFilter,
  IFilterItem,
  ISelectedFilter,
} from './filter.types';

@Component({
  selector: 'filter-panel',
  templateUrl: './filter-panel.component.html',
  styleUrls: ['./filter-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FilterPanelComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  @Input() public showResetButton: boolean = false;
  /**
   * use this with innerShowResetAllButton to judge is need showResetAll button
   * will use innerShowResetAllButton when this is null or undefined
   */
  @Input() public showResetAllButton: boolean;
  @Input() public showCancelButton: boolean = true;
  @Input() public isFullScreen: boolean = true;
  @Input()
  public set filters(data: IFilter[]) {
    this.filtersList = clone(data);

    if (
      !this.defaultFiltersList ||
      (isEmpty(this.defaultFiltersList) && !isEmpty(this.filtersList)) ||
      !isEqual(this.defaultFiltersList, this.filtersList)
    ) {
      this.defaultFiltersList = this.filtersList;
    }

    this.innerShowResetAllButton = this.isFilterChanged();
  }

  @Output() public applyFilters: EventEmitter<FilterPanelUpdates> =
    new EventEmitter();
  @Output() public filtersCounter: EventEmitter<number> = new EventEmitter();
  @Output() public resetFilters = new EventEmitter<IFilter[]>();

  @lazyInject(FormBuilder) public fb: FormBuilder;

  public innerShowResetAllButton = false;

  public e2eAttrs: typeof E2E_ATTRIBUTES = E2E_ATTRIBUTES;

  public filterTypes = FILTER_PANEL_TYPES;

  public filtersList: IFilter[] = [];
  public defaultFiltersList: IFilter[];
  public filterForm: FormGroup;
  public isFilterPanelShown: boolean = false;
  public filterCounter: number = 0;

  private reservedFormValue: FormGroup;

  public constructor(protected injector: Injector) {
    super(injector);
  }

  public resetAll(): void {
    this.filtersList = this.defaultFiltersList || [];
    this.innerShowResetAllButton = false;
    this.resetFilters.emit(this.filtersList);
    this.setFilterFormControls();
    this.onSubmit();
  }

  public ngOnInit() {
    this.emitFiltersValue(FILTER_PANEL_UPDATE_TYPES.UPDATE);
  }

  public ngOnChanges(): void {
    this.setFilterFormControls();
    this.updateCounter(this.filterForm.value);
    this.emitFiltersValue(FILTER_PANEL_UPDATE_TYPES.UPDATE);
  }

  public isShowResetAllButton(): boolean {
    return this.showResetAllButton ?? this.innerShowResetAllButton;
  }

  public buildFormGroup(): { [key: string]: FormGroup } {
    const filtersGroup = {};
    forEach(this.filtersList, (filterItem: IFilter) => {
      const filterItems = {};
      switch (filterItem.type) {
        case FILTER_PANEL_TYPES.SINGLE_VALUE:
          filterItems[FILTER_PANEL_TYPES.SINGLE_VALUE] = new FormControl(
            chain(filterItem.items).find('selected').get('id').value(),
          );
          break;
        default:
          filterItem.items?.forEach((item: IFilterItem) => {
            if (item.id) {
              filterItems[item.id] = new FormControl(item.selected);
            }

            if (isArray(item.items)) {
              item.items.forEach((childItem: IFilterItem) => {
                if (childItem.id) {
                  filterItems[childItem.id] = new FormControl(
                    childItem.selected,
                  );
                }
              });
            }
          });
          break;
      }

      filtersGroup[filterItem.id] = this.fb.group(filterItems);
    });
    return filtersGroup;
  }

  public initFilterForm(filtersGroup: object): void {
    this.filterForm = this.fb.group(filtersGroup);
  }

  public updateFilterForm(filtersGroup: object): void {
    Object.keys(filtersGroup).forEach((key: string) => {
      const group: FormGroup = filtersGroup[key];
      const formGroup = <FormGroup>this.filterForm.controls[key];

      if (isNil(formGroup)) {
        this.filterForm.addControl(key, group);
        return;
      }

      Object.keys(group.controls).forEach((controlKey: string) => {
        const control: AbstractControl = group.controls[controlKey];
        if (formGroup.controls[controlKey]) {
          formGroup.controls[controlKey].patchValue(control.value, {
            emitEvent: false,
          });
        } else {
          formGroup.addControl(controlKey, control);
        }
      });
      Object.keys(formGroup.controls).forEach((controlKey: string) => {
        if (!group.contains(controlKey)) {
          formGroup.removeControl(controlKey);
        }
      });
    });
  }

  public setFilterFormControls(): void {
    const filtersGroup = this.buildFormGroup();
    if (this.filterForm) {
      this.updateFilterForm(filtersGroup);
    } else {
      this.initFilterForm(filtersGroup);
    }
  }

  public updateCounter(data: object): void {
    const filters = this.selectedFilters(data);

    const count = reduce(
      filters,
      (accCount, curFilter) => {
        const isFilterEmpty = curFilter.selected.length === 0;
        const isPartialSelected =
          !!curFilter.scope && curFilter.selected.length < curFilter.scope;
        const isDateTimeRange = /time|date/i.test(curFilter.id);

        return isFilterEmpty || isPartialSelected || isDateTimeRange
          ? accCount + 1
          : accCount;
      },
      0,
    );

    this.filterCounter = count;
    this.filtersCounter.emit(count);
  }

  public toggleFilterPanel(): void {
    this.isFilterPanelShown = !this.isFilterPanelShown;
  }

  public closeFilterPanel(): void {
    this.isFilterPanelShown = this.isFilterPanelShown && false;
  }

  public onOverlayClick(): void {
    this.closeFilterPanel();
  }

  public onSubmit(): void {
    this.emitFiltersValue(FILTER_PANEL_UPDATE_TYPES.SUBMIT);
    this.closeFilterPanel();
    this.updateCounter(this.filterForm.value);
    this.updateFilterList();
  }

  public onCancel(): void {
    this.emitFiltersValue(FILTER_PANEL_UPDATE_TYPES.CANCEL);
    this.closeFilterPanel();
  }

  public selectedFilters(data: object): ISelectedFilter[] {
    const filterResults: ISelectedFilter[] = [];

    if (isEmpty(this.filtersList)) {
      return this.normalizeFilterResults(filterResults);
    }

    Object.keys(data).forEach((tpe: string) => {
      const category: ISelectedFilter = <ISelectedFilter>{};
      let checkboxes = {};
      const selected: string[] = [];
      const labels: string[] = [];

      const filterType = this.filtersList.find(
        (filterItem: IFilter) => filterItem.id === tpe,
      )?.type;
      category.id = tpe;
      checkboxes = data[tpe];
      Object.keys(checkboxes).forEach((item: string) => {
        switch (filterType) {
          case FILTER_PANEL_TYPES.SINGLE_VALUE:
            selected.push(checkboxes[item]);
            break;
          default:
            if (checkboxes[item] === true) {
              selected.push(item);

              let parameter: IFilterItem;
              const filtersItem: IFilterItem = this.filtersList.find(
                (f) => f.id === category.id,
              );

              filtersItem.items?.forEach((i: IFilterItem) => {
                if (i.id === item || i._id === item) {
                  parameter = i;
                }

                if (isArray(i.items)) {
                  i.items.forEach((childItem: IFilterItem) => {
                    if (childItem.id === item || childItem._id === item) {
                      parameter = childItem;
                    }
                  });
                }
              });
              labels.push(parameter?.name() ?? '');
            }
            break;
        }
      });
      category.selected = selected;
      category.labels = labels;
      category.scope = Object.keys(checkboxes).length;
      filterResults.push(category);
    });
    return this.normalizeFilterResults(filterResults);
  }

  protected updateFilterList(): void {
    const skippedFilterTypes = [];

    this.filtersList = getUpdatedFilterList(
      this.filterForm.value,
      this.filtersList,
      skippedFilterTypes,
    );

    this.innerShowResetAllButton = this.isFilterChanged();
  }

  protected emitFiltersValue(updateType: FILTER_PANEL_UPDATE_TYPES): void {
    if (updateType === FILTER_PANEL_UPDATE_TYPES.CANCEL) {
      this.filterForm.setValue(this.reservedFormValue);
    }

    const filtersValue: ISelectedFilter[] = this.selectedFilters(
      this.filterForm.value,
    );
    this.applyFilters.emit({ payload: filtersValue, type: updateType });

    this.reservedFormValue = this.filterForm.value;
  }

  protected normalizeFilterResults(
    filterResults: ISelectedFilter[],
  ): ISelectedFilter[] {
    return filterResults.map((item) => {
      switch (item.id) {
        default:
          return item;
      }
    });
  }

  protected isFilterChanged(): boolean {
    return !isEqual(this.defaultFiltersList, this.filtersList);
  }
}
